import React, { useMemo, useState } from 'react';
import './AiVisibilityReportForm.scss';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { differenceInCalendarDays, format } from 'date-fns';
import type { AiVisibilityReportUsageResponse } from '@brand24-original/llm-types';

const MAX_DAYS = 31;

interface AiVisibilityReportFormProps {
  isBusy: boolean;
  onGenerate: (from: string, to: string) => void;
  error?: string | null;
  minDate?: Date;
  maxDate?: Date;
  /** Account-wide monthly quota; undefined while loading — the form stays usable, backend guards anyway. */
  usage?: AiVisibilityReportUsageResponse;
}

const AiVisibilityReportForm: React.FC<AiVisibilityReportFormProps> = ({
  isBusy,
  onGenerate,
  error,
  minDate,
  maxDate,
  usage,
}) => {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<Date[] | null>(null);
  const isQuotaExhausted = usage != null && usage.remaining <= 0;
  const resetsAtLabel = useMemo(
    () =>
      usage ? new Date(usage.resetsAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long' }) : '',
    [usage, i18n.language]
  );

  const validationError = useMemo(() => {
    if (!range || range.length < 2 || !range[0] || !range[1]) {
      return null;
    }
    const days = differenceInCalendarDays(range[1], range[0]) + 1;
    if (days > MAX_DAYS) {
      return t('aiVisibilityReport.periodTooLong', { max: MAX_DAYS });
    }
    return null;
  }, [range, t]);

  const canSubmit =
    !isBusy &&
    !isQuotaExhausted &&
    !!range &&
    range.length === 2 &&
    !!range[0] &&
    !!range[1] &&
    !validationError;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit || !range?.[0] || !range?.[1]) {
      return;
    }
    onGenerate(format(range[0], 'yyyy-MM-dd'), format(range[1], 'yyyy-MM-dd'));
  };

  return (
    <form className="ai-visibility-report-form" onSubmit={handleSubmit}>
      <div className="ai-visibility-report-form__row">
        <div className="ai-visibility-report-form__period">
          <span className="ai-visibility-report-form__period-label">
            {t('aiVisibilityReport.periodLabel')}:
          </span>
          <Calendar
            value={range}
            onChange={(e) => setRange(e.value as Date[] | null)}
            selectionMode="range"
            readOnlyInput
            hideOnRangeSelection
            dateFormat="yy-mm-dd"
            minDate={minDate}
            maxDate={maxDate ?? new Date()}
            disabled={isBusy}
            pt={{
              root: { className: 'calendar' },
              panel: { className: 'calendar-panel' },
            }}
          />
        </div>
        <Button
          type="submit"
          className="btn btn--sm"
          label={isBusy ? t('aiVisibilityReport.generating') : t('aiVisibilityReport.generateBtn')}
          disabled={!canSubmit}
        />
      </div>

      {usage && (
        <span
          className={`ai-visibility-report-form__quota${
            isQuotaExhausted ? ' ai-visibility-report-form__quota--exhausted' : ''
          }`}
        >
          {isQuotaExhausted
            ? t('aiVisibilityReport.quotaExhausted', { limit: usage.limit, date: resetsAtLabel })
            : t('aiVisibilityReport.quotaRemaining', {
                remaining: usage.remaining,
                limit: usage.limit,
                date: resetsAtLabel,
              })}
        </span>
      )}

      {(validationError || error) && (
        <span className="form__error">{validationError || error}</span>
      )}
    </form>
  );
};

export default AiVisibilityReportForm;
